import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const API = process.env.REACT_APP_BACKEND_URL;

export default function Waitlist({ t, locale, user }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [motivation, setMotivation] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | joined | exists | error | invalid

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) || !motivation.trim()) {
      setStatus("invalid");
      return;
    }
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/api/waitlist`, {
        name: name.trim(),
        email: email.trim(),
        city: city.trim(),
        motivation: motivation.trim(),
        locale,
      });
      setStatus(res.data.status === "exists" ? "exists" : "joined");
    } catch {
      setStatus("error");
    }
  };

  const done = status === "joined" || status === "exists";
  const alreadyJoined = !!user?.waitlist_joined && !done;

  return (
    <section id="waitlist" data-testid="waitlist-section" className="relative border-t border-black/10 bg-surface">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 py-28 md:grid-cols-2 md:py-40 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: EASE }}
        >
          <p data-testid="waitlist-overline" className="mb-4 font-display text-xs font-bold uppercase tracking-[0.25em] text-flame md:text-sm">
            {t.overline}
          </p>
          <h2 data-testid="waitlist-heading" className="mb-8 font-display text-4xl font-bold tracking-tight text-bone sm:text-5xl lg:text-6xl">
            {t.heading}
          </h2>
          <p data-testid="waitlist-sub" className="max-w-md text-base leading-relaxed text-ash md:text-lg">
            {t.sub}
          </p>

        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
        >
          <AnimatePresence mode="wait">
            {done || alreadyJoined ? (
              <motion.div
                key="success"
                data-testid="waitlist-success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="flex h-full min-h-[380px] flex-col items-start justify-center border border-flame/40 bg-flame/5 p-10"
              >
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.2 }}
                >
                  <CheckCircle2 size={52} className="text-flame" />
                </motion.span>
                <h3 className="mt-8 font-display text-3xl font-bold text-bone sm:text-4xl">
                  {status === "exists" || alreadyJoined ? t.existsTitle : t.successTitle}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-ash md:text-lg">
                  {status === "exists" || alreadyJoined ? t.existsText : t.successText}
                </p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                data-testid="waitlist-form"
                onSubmit={submit}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col gap-10"
              >
                <Field
                  id="waitlist-name-input"
                  label={t.nameLabel}
                  value={name}
                  onChange={setName}
                  placeholder={t.namePlaceholder}
                  autoComplete="name"
                />
                <Field
                  id="waitlist-email-input"
                  label={t.emailLabel}
                  type="email"
                  value={email}
                  onChange={setEmail}
                  placeholder={t.emailPlaceholder}
                  autoComplete="email"
                  disabled={!!user}
                  note={user ? t.verified : undefined}
                />
                <Field
                  id="waitlist-city-input"
                  label={t.cityLabel}
                  value={city}
                  onChange={setCity}
                  placeholder={t.cityPlaceholder}
                  autoComplete="address-level2"
                />
                <div>
                  <label htmlFor="waitlist-motivation-input" className="mb-2 block font-display text-xs font-bold uppercase tracking-[0.2em] text-ash">
                    {t.motivationLabel}
                  </label>
                  <textarea
                    id="waitlist-motivation-input"
                    data-testid="waitlist-motivation-input"
                    rows={3}
                    value={motivation}
                    onChange={(e) => setMotivation(e.target.value)}
                    placeholder={t.motivationPlaceholder}
                    className="w-full resize-none border-b border-black/20 bg-transparent py-3 text-base text-bone outline-none transition-colors duration-300 placeholder:text-ash/40 focus:border-flame md:text-lg"
                  />
                </div>

                {(status === "error" || status === "invalid") && (
                  <p data-testid="waitlist-error" className="text-sm text-red-400">
                    {status === "invalid" ? t.invalidText : t.errorText}
                  </p>
                )}

                <motion.button
                  data-testid="waitlist-submit-btn"
                  type="submit"
                  disabled={status === "loading"}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="mt-2 inline-flex w-fit items-center gap-3 rounded-full bg-flame px-10 py-4 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-flame-hover disabled:opacity-60"
                >
                  {status === "loading" && <Loader2 size={16} className="animate-spin" />}
                  {status === "loading" ? t.submitting : t.submit}
                </motion.button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}

function Field({ id, label, value, onChange, placeholder, type = "text", autoComplete, disabled = false, note }) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block font-display text-xs font-bold uppercase tracking-[0.2em] text-ash">
        {label}
      </label>
      <input
        id={id}
        data-testid={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        className="w-full border-b border-black/20 bg-transparent py-3 text-base text-bone outline-none transition-colors duration-300 placeholder:text-ash/40 focus:border-flame disabled:opacity-60 md:text-lg"
      />
      {note && (
        <p data-testid={`${id}-note`} className="mt-2 text-sm font-medium text-flame">
          {note}
        </p>
      )}
    </div>
  );
}
